import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog'
import { useLabelQR } from '../../hooks/useLabels'

/**
 * LabelQRPreview shows the label's actual QR, not a stand-in icon.
 *
 * The point is to be able to point a phone at the screen: it is the cheapest
 * confirmation that a code scans, and until this existed the only way to see a
 * QR at all was to spend a sheet of label stock on it.
 *
 * The SVG is inlined via dangerouslySetInnerHTML rather than loaded through an
 * <img src>, because the endpoint is authenticated by header — an image request
 * would not carry the token. The markup comes from our own backend, is a
 * generated QR matrix with no scripting, and is never influenced by user input
 * beyond a 7-character code drawn from a fixed alphabet.
 */
export function LabelQRPreview({
    org,
    labelUid,
    code,
    size = 'sm',
}: {
    org: string
    labelUid: string
    code: string
    size?: 'sm' | 'lg'
}) {
    const { t } = useTranslation('labels')
    const [open, setOpen] = useState(false)
    const { data: svg, isLoading, isError } = useLabelQR(org, labelUid)

    const box = size === 'lg' ? 'h-48 w-48' : 'h-12 w-12'

    if (isLoading) {
        return <div className={`${box} shrink-0 animate-pulse rounded-sm bg-muted`} aria-hidden="true" />
    }

    if (isError || !svg) {
        return (
            <div
                className={`${box} shrink-0 rounded-sm border border-dashed border-border`}
                title={t('card.qr_unavailable')}
                data-testid={`label-qr-error-${code}`}
            />
        )
    }

    return (
        <>
            <button
                type="button"
                onClick={() => setOpen(true)}
                aria-label={t('card.qr_enlarge', { code })}
                title={t('card.qr_enlarge', { code })}
                className={`${box} shrink-0 rounded-sm border border-border bg-white p-0.5 [&>svg]:h-full [&>svg]:w-full`}
                data-testid={`label-qr-${code}`}
                dangerouslySetInnerHTML={{ __html: svg }}
            />

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent data-testid="label-qr-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-mono">{code}</DialogTitle>
                        <DialogDescription>{t('card.qr_dialog_help')}</DialogDescription>
                    </DialogHeader>
                    <div
                        className="mx-auto w-64 rounded-sm border border-border bg-white p-3 [&>svg]:h-full [&>svg]:w-full"
                        dangerouslySetInnerHTML={{ __html: svg }}
                    />
                </DialogContent>
            </Dialog>
        </>
    )
}
